import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { useAuth } from 'hooks/UseAuth'
import { optimisticUpdateFileInInfiniteLists } from 'hooks/queries/files/cacheUtils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AlmanacFileCalendarItem } from 'hooks/queries/calendar/types'
import { type AlmanacCableConsumer, type AlmanacCableSubscription } from 'providers/CableConsumer/types'

export interface ArticlePreviewUpdate {
  op: 'preview_update'
  image_url: string
  duration: number | null
}

export interface ArticlePreviewUrlStatus {
  op: 'preview_url_out_of_date'
  image_url_generating: boolean
  duration: number | null
}

export const useArticlePreviewSync = (eventsData: AlmanacFileCalendarItem[]) => {
  const auth = useAuth()
  const websocketConnection = useWebsocketConnection()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const subscriptionsRef = useRef<Map<string, AlmanacCableSubscription>>(new Map())
  const activeConnectionRef = useRef<AlmanacCableConsumer | null>(null)

  const serializedArticleKeys = useMemo(() => {
    const articleKeys = new Set<string>()
    eventsData.forEach((event) => {
      if (event.asset_type === 'Article' && event.key) {
        articleKeys.add(event.key)
      }
    })

    return JSON.stringify(Array.from(articleKeys).sort())
  }, [eventsData])

  const unsubscribeAll = useCallback(() => {
    subscriptionsRef.current.forEach((subscription) => {
      subscription.unsubscribe()
    })
    subscriptionsRef.current.clear()
    activeConnectionRef.current = null
  }, [])

  useEffect(() => {
    return () => {
      unsubscribeAll()
    }
  }, [unsubscribeAll])

  useEffect(() => {
    if (!auth || !websocketConnection) {
      unsubscribeAll()
      return
    }

    if (activeConnectionRef.current && activeConnectionRef.current !== websocketConnection) {
      unsubscribeAll()
    }

    activeConnectionRef.current = websocketConnection

    const articleKeys = JSON.parse(serializedArticleKeys) as string[]
    const activeArticleKeys = new Set(articleKeys)
    const subscriptions = subscriptionsRef.current

    subscriptions.forEach((subscription, key) => {
      if (activeArticleKeys.has(key)) return

      subscription.unsubscribe()
      subscriptions.delete(key)
    })

    articleKeys.forEach((key) => {
      if (subscriptions.has(key)) return

      const subscription = websocketConnection.subscriptions.create(
        {
          channel: 'ArticlePreviewChannel',
          key,
        },
        {
          received(data: ArticlePreviewUpdate | ArticlePreviewUrlStatus) {
            if (data.op === 'preview_update') {
              optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, key, (f) => ({
                ...f,
                image_url: data.image_url,
                image_url_generating: false,
                duration: data.duration || f.duration,
              }))
            } else if (data.op === 'preview_url_out_of_date') {
              optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, key, (f) => ({
                ...f,
                image_url_generating: true,
              }))
            }
          },
        },
      )

      subscriptions.set(key, subscription)
    })
  }, [auth, websocketConnection, serializedArticleKeys, queryClient, workspaceId, unsubscribeAll])
}
